
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import FeedbackManagement from "@/components/admin/FeedbackManagement";
import RoadmapManagement from "@/components/admin/RoadmapManagement";
import { BoardManagement } from "@/components/admin/BoardManagement";
import { ChangelogManagement } from "@/components/admin/ChangelogManagement";
import { TeamManagement } from "@/components/admin/TeamManagement";
import KnowledgeBaseManagement from "@/components/admin/KnowledgeBaseManagement";
import { PlanManagement } from "@/components/admin/PlanManagement";
import { ChatManagement } from "@/components/admin/ChatManagement";
import { ContactsManagement } from "@/components/admin/ContactsManagement";
import { useFeedbackData } from "@/hooks/useFeedbackData";
import CompanySettings from "@/components/company/CompanySettings";
import StatusManagement from "@/components/admin/status/StatusManagement";
import { useSearchParams, useNavigate } from "react-router-dom";

type DashboardTabsProps = {
  company: any;
  feedback: any[];
  isAtLimit: boolean;
  onFeedbackUpdate: () => Promise<void>;
  onCompanyUpdate: () => Promise<void>;
  currentUserId?: string;
};

export function DashboardTabs({
  company,
  feedback,
  isAtLimit,
  onFeedbackUpdate,
  onCompanyUpdate,
  currentUserId
}: DashboardTabsProps) {
  const { companyStatuses, refetchCompanyStatuses } = useFeedbackData(company.id);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const activeTab = searchParams.get('tab') || 'feedback';

  const handleTabChange = (value: string) => {
    navigate(`/admin?tab=${value}`);
  };

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">

      <TabsContent value="feedback" className="space-y-4">
        <FeedbackManagement 
          feedback={feedback} 
          onFeedbackUpdate={onFeedbackUpdate}
          companyStatuses={companyStatuses}
          companyId={company.id} // Pass the company ID to FeedbackManagement
        />
      </TabsContent>

      <TabsContent value="roadmap" className="space-y-4">
        <RoadmapManagement 
          companyId={company.id} 
          feedback={feedback} 
          companyStatuses={companyStatuses}
          onFeedbackUpdate={onFeedbackUpdate}
          companyPlan={company.plan}
        />
      </TabsContent>

      <TabsContent value="boards" className="space-y-4">
        <BoardManagement 
          companyId={company.id}
          companyPlan={company.plan}
          boards={[]}
          onBoardsUpdate={onFeedbackUpdate}
        />
      </TabsContent>
      
      <TabsContent value="statuses" className="space-y-4">
        <div>
          <h3 className="text-lg font-medium">Feedback Statuses</h3>
          <Separator className="my-4" />
          <StatusManagement 
            companyId={company.id}
            onStatusesUpdate={refetchCompanyStatuses}
          />
        </div>
      </TabsContent>

      <TabsContent value="changelog" className="space-y-4">
        <ChangelogManagement 
          companyId={company.id}
          companyPlan={company.plan}
        />
      </TabsContent>

      <TabsContent value="knowledge" className="space-y-4">
        <KnowledgeBaseManagement 
          companyId={company.id} 
        />
      </TabsContent>

      <TabsContent value="team" className="space-y-4">
        <TeamManagement 
          companyId={company.id}
          isPro={company.plan !== 'free'}
        />
      </TabsContent>

      <TabsContent value="chat" className="space-y-4">
        <ChatManagement
          companyId={company.id}
          userId={currentUserId || company.owner_id}
          userName={company.name}
          isPlanAllowed={company.plan === 'starter' || company.plan === 'enterprise'}
          companyPlan={company.plan}
        />
      </TabsContent>

      <TabsContent value="billing" className="space-y-4">
        <div>
          <h3 className="text-lg font-medium">Billing & Plan</h3>
          <Separator className="my-4" />
          <PlanManagement
            company={company}
            onCompanyUpdate={onCompanyUpdate}
          />
        </div>
      </TabsContent>

      <TabsContent value="settings" className="space-y-4">
        <div>
          <h3 className="text-lg font-medium">Company Settings</h3>
          <Separator className="my-4" />
          <CompanySettings
            company={company}
            onUpdate={onCompanyUpdate}
          />
        </div>
      </TabsContent>

      <TabsContent value="contacts" className="space-y-4">
        <ContactsManagement companyId={company.id} />
      </TabsContent>
    </Tabs>
  );
}
